import React, { useState, useMemo } from 'react';
import { X, Check, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { FieldDef, ColumnMapping } from '../../lib/campaignIntelligence';

interface ColumnMapperProps {
  isOpen: boolean;
  type: 'meta' | 'rt';
  headers: string[];
  fields: FieldDef[];
  initialMapping: ColumnMapping;
  onConfirm: (mapping: ColumnMapping) => void;
  onCancel: () => void;
}

export function ColumnMapper({ isOpen, type, headers, fields, initialMapping, onConfirm, onCancel }: ColumnMapperProps) {
  const [mapping, setMapping] = useState<ColumnMapping>(initialMapping);

  const usedCols = useMemo(() => Object.values(mapping).filter(Boolean) as string[], [mapping]);

  const requiredMapped = useMemo(
    () => fields.filter(f => f.required).every(f => !!mapping[f.key]),
    [fields, mapping]
  );

  const mappedCount = useMemo(() => Object.values(mapping).filter(Boolean).length, [mapping]);

  if (!isOpen) return null;

  const assign = (fieldKey: string, col: string) => {
    setMapping(prev => ({
      ...prev,
      [fieldKey]: prev[fieldKey] === col ? null : col,
    }));
  };

  const requiredFields = fields.filter(f => f.required);
  const optionalFields = fields.filter(f => !f.required);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-card border border-border rounded-xl w-full max-w-[780px] max-h-[88vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-base font-semibold text-foreground">
              Map Columns — {type === 'meta' ? 'Meta Ads' : 'RedTrack'}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Click the column from your file that corresponds to each field. Green = assigned.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onCancel}>
            <X size={14} className="mr-1" /> Cancel
          </Button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {[{ title: 'Required Fields', items: requiredFields }, { title: 'Optional Fields', items: optionalFields }].map(section => (
            section.items.length > 0 && (
              <div key={section.title} className="mb-5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3 pb-2 border-b border-border">
                  {section.title}
                </div>
                {section.items.map(f => {
                  const assigned = mapping[f.key];
                  return (
                    <div
                      key={f.key}
                      className={`flex items-start gap-3 mb-2 p-3 rounded-lg border transition-colors ${
                        assigned
                          ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/30'
                          : 'border-border bg-muted/50'
                      }`}
                    >
                      <div className="w-[150px] flex-shrink-0 pt-0.5">
                        <div className="text-xs font-semibold text-foreground">
                          {f.label}
                          {f.required ? (
                            <span className="text-[9px] text-red-500 ml-1">*</span>
                          ) : (
                            <span className="text-[9px] text-muted-foreground ml-1">opt</span>
                          )}
                        </div>
                        <div className="text-[9px] text-muted-foreground mt-0.5">{f.hint}</div>
                      </div>
                      <ArrowRight size={12} className="text-muted-foreground flex-shrink-0 mt-1.5" />
                      <div className="flex flex-wrap gap-1.5 flex-1">
                        {headers.map(col => {
                          const isSelected = assigned === col;
                          const isUsedElsewhere = !isSelected && usedCols.includes(col);
                          return (
                            <button
                              key={col}
                              onClick={() => !isUsedElsewhere && assign(f.key, col)}
                              disabled={isUsedElsewhere}
                              className={`px-2.5 py-1 rounded text-[10px] font-mono border transition-all ${
                                isSelected
                                  ? 'border-green-500 dark:border-green-600 text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/50 font-bold'
                                  : isUsedElsewhere
                                  ? 'opacity-25 cursor-not-allowed border-border bg-muted text-muted-foreground'
                                  : 'border-border bg-card text-muted-foreground hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 cursor-pointer'
                              }`}
                            >
                              {col}{isSelected && ' ✓'}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between flex-shrink-0">
          <div className="text-xs text-muted-foreground">
            <span className="font-semibold text-green-600 dark:text-green-400">{mappedCount}</span> of {fields.length} fields mapped
            {!requiredMapped && ' — required fields still need mapping'}
          </div>
          <Button
            onClick={() => onConfirm(mapping)}
            disabled={!requiredMapped}
          >
            <Check size={14} className="mr-1.5" /> Apply Mapping
          </Button>
        </div>
      </div>
    </div>
  );
}
